import { ChannelTemplateChannel } from '../types/channel-template.type'
import { InvitationChannelHistoryState, InvitationChannelInviteEntry, InvitationCollectionItem } from '../types/invitation-tracker.type'

export type InvitationChannel = 'email' | 'whatsapp' | 'sms'

export type InvitationChannelStatusColor = 'green' | 'yellow' | 'red'

/** Invitation (channel) history tone — separate from Action taken badges. */
export type InvitationHistoryStatusTone = 'delivered' | 'pending' | 'failed' | 'unknown'

export type InvitationChannelStatusIndicator = {
  channel: InvitationChannel
  color: InvitationChannelStatusColor | null
  status: string | null
}

const INVITATION_CHANNEL_ORDER: InvitationChannel[] = ['email', 'whatsapp', 'sms']

export const INVITATION_CHANNEL_TO_API: Record<InvitationChannel, ChannelTemplateChannel> = {
  email: 'EMAIL',
  whatsapp: 'WHATSAPP',
  sms: 'SMS'
}

/** Red statuses every channel can report. */
const SHARED_FAILED_STATUS_TONE: Readonly<Record<string, InvitationHistoryStatusTone>> = {
  failed: 'failed',
  'payment failed': 'failed',
  bounced: 'failed',
  'opt out': 'failed'
}

/**
 * Channel invitation status → UI tone. Every channel also maps {@link SHARED_FAILED_STATUS_TONE} (Failed / Payment failed / Bounced / Opt out) to red.
 * Email: Delivered → green; Sent / Queued → yellow; Complaint / Error → red
 * WhatsApp: Delivered / Read → green; Sent / Queued → yellow; Complaint / Error → red
 * SMS: Received → green; Pending → yellow; Not supported → red
 */
const INVITATION_CHANNEL_STATUS_TONE: Record<InvitationChannel, Readonly<Record<string, InvitationHistoryStatusTone>>> = {
  email: {
    ...SHARED_FAILED_STATUS_TONE,
    delivered: 'delivered',
    sent: 'pending',
    queued: 'pending',
    complaint: 'failed',
    error: 'failed'
  },
  whatsapp: {
    ...SHARED_FAILED_STATUS_TONE,
    delivered: 'delivered',
    read: 'delivered',
    sent: 'pending',
    queued: 'pending',
    complaint: 'failed',
    error: 'failed'
  },
  sms: {
    ...SHARED_FAILED_STATUS_TONE,
    received: 'delivered',
    pending: 'pending',
    'not supported': 'failed'
  }
}

const INVITATION_HISTORY_STATUS_COLOR: Record<Exclude<InvitationHistoryStatusTone, 'unknown'>, InvitationChannelStatusColor> = {
  delivered: 'green',
  pending: 'yellow',
  failed: 'red'
}

/**
 * Builds a lowercase lookup key for invitation / history status strings.
 * @param status {string | null | undefined} - Status from the API.
 */
export function invitationStatusLookupKey(status: string | null | undefined): string {
  if (!status) return ''
  return status.trim().toLowerCase().replace(/[_-]+/g, ' ')
}

/**
 * Formats an invitation / history status for badge display (e.g. "opt-out" → "opt out").
 * Pair with `tw-capitalize` in the template for title case.
 * @param status {string | null | undefined} - Status from the API.
 */
export function formatInvitationHistoryStatusLabel(status: string | null | undefined): string {
  return invitationStatusLookupKey(status)
}

/**
 * Maps a channel invitation status to a UI tone (not Action taken).
 * @param status {string} - Invitation status from the API.
 * @param channel {InvitationChannel} - Channel the status belongs to.
 */
export function invitationHistoryStatusTone(status: string, channel: InvitationChannel): InvitationHistoryStatusTone {
  return INVITATION_CHANNEL_STATUS_TONE[channel][invitationStatusLookupKey(status)] ?? 'unknown'
}

/**
 * Maps a channel invitation status to the bulb color for that channel.
 * Unknown statuses return null so no indicator is shown.
 * @param status {string} - Latest channel invite status from the API.
 * @param channel {InvitationChannel} - Channel the status belongs to.
 */
export function invitationChannelStatusColor(status: string, channel: InvitationChannel): InvitationChannelStatusColor | null {
  const tone = invitationHistoryStatusTone(status, channel)
  if (tone === 'unknown') return null
  return INVITATION_HISTORY_STATUS_COLOR[tone]
}

/**
 * Picks the latest invite entry for a channel (backend returns newest first).
 * @param entries {InvitationChannelInviteEntry[] | undefined} - Channel invite history.
 */
export function getLatestInvitationChannelEntry(entries: InvitationChannelInviteEntry[] | undefined): InvitationChannelInviteEntry | null {
  if (!entries?.length) return null
  return entries[0]
}

/**
 * Raw latest invitation status for a channel on a collection item (not the bulb override).
 * @param item {InvitationCollectionItem} - Collection item from the API.
 * @param channel {InvitationChannel} - Channel to read.
 */
export function getInvitationChannelLatestStatus(item: InvitationCollectionItem, channel: InvitationChannel): string | null {
  return getLatestInvitationChannelEntry(item.invite_channel[INVITATION_CHANNEL_TO_API[channel]])?.status ?? null
}

/**
 * Returns the loaded channel history only when it belongs to the given collection item + channel.
 * @param history {InvitationChannelHistoryState | null} - Shared channel history state.
 * @param collectionItemId {number} - Collection item the panel is showing.
 * @param channel {InvitationChannel} - Channel the panel is showing.
 */
export function getActiveInvitationChannelHistory(
  history: InvitationChannelHistoryState | null,
  collectionItemId: number,
  channel: InvitationChannel
): InvitationChannelHistoryState | null {
  if (!history || history.collectionItemId !== collectionItemId || history.channel !== channel) return null
  return history
}

/**
 * Status used for the channel indicator bulb.
 * A delivered (green) outcome always wins — even when a newer failed/pending status follows (e.g. opt-out after delivered).
 * @param entries {InvitationChannelInviteEntry[] | undefined} - Channel invite history.
 * @param channel {InvitationChannel} - Channel the entries belong to.
 */
export function getInvitationChannelIndicatorStatus(entries: InvitationChannelInviteEntry[] | undefined, channel: InvitationChannel): string | null {
  const latest = getLatestInvitationChannelEntry(entries)
  if (!latest?.status) return null

  const deliveredEntry = entries?.find((entry) => invitationHistoryStatusTone(entry.status, channel) === 'delivered')
  return deliveredEntry?.status ?? latest.status
}

/**
 * Builds per-channel status indicators from the collection item's `invite_channel` payload.
 * @param item {InvitationCollectionItem} - Collection item from the API.
 */
export function getInvitationCollectionChannelStatuses(item: InvitationCollectionItem): InvitationChannelStatusIndicator[] {
  return INVITATION_CHANNEL_ORDER.map((channel) => {
    const status = getInvitationChannelIndicatorStatus(item.invite_channel[INVITATION_CHANNEL_TO_API[channel]], channel)
    return {
      channel,
      status,
      color: status ? invitationChannelStatusColor(status, channel) : null
    }
  })
}

//TODO: Remove this once SMS is supported
export const COMING_SOON_INVITATION_CHANNELS: ReadonlySet<InvitationChannel> = new Set<InvitationChannel>(['sms'])

export const INVITATION_CHANNEL_COMING_SOON_TOOLTIP = 'Coming soon'

/**
 * TODO: Remove this once SMS is supported
 * Returns true when the channel is not yet supported.
 * @param channel {InvitationChannel} - Channel to check.
 */
export function isInvitationChannelComingSoon(channel: InvitationChannel): boolean {
  return COMING_SOON_INVITATION_CHANNELS.has(channel)
}

/**
 * Returns true when the channel is supported and the invitee has the contact details it requires.
 * Email needs an email; WhatsApp and SMS need a phone number.
 * @param item {InvitationCollectionItem} - Collection item from the API.
 * @param channel {InvitationChannel} - Channel to check.
 */
export function isInvitationChannelAvailable(item: InvitationCollectionItem, channel: InvitationChannel): boolean {
  if (isInvitationChannelComingSoon(channel)) return false

  switch (channel) {
    case 'email':
      return Boolean(item.email?.trim())
    case 'whatsapp':
    case 'sms':
      return Boolean(item.phone_number?.trim())
  }
}

/**
 * SVG path fill for channel icons — gray-700 when available, gray-400 when disabled.
 * @param available {boolean} - Whether the channel has required contact details.
 */
export function invitationChannelIconFill(available: boolean): string {
  return available ? '#374151' : '#9CA3AF'
}

/**
 * Returns Tailwind classes for a channel status bulb.
 * @param color {InvitationChannelStatusColor} - Dot color from the latest channel invitation status.
 */
export function invitationChannelDotClass(color: InvitationChannelStatusColor): string {
  switch (color) {
    case 'green':
      return 'tw-bg-green-500'
    case 'yellow':
      return 'tw-bg-yellow-400'
    case 'red':
      return 'tw-bg-red-600'
  }
}
